import {
  cloneElement,
  CSSProperties,
  HTMLProps,
  isValidElement,
  ReactElement,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getDOMProps } from 'app/shared/utils/react';
import styled, { css } from 'styled-components';

import { IconCheck } from '@u21/tabler-icons';
import { Link } from 'react-router-dom';
import { Tab, TabProps, Tabs } from '@mui/material';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import {
  U21MenuButton,
  U21MenuButtonItems,
} from 'app/shared/u21-ui/components/display/U21MenuButton';

export type TabValue = string | number;

export interface U21TabItem<TValue extends TabValue = TabValue> extends Omit<
  TabProps,
  'component' | 'label' | 'value'
> {
  link?: boolean;
  search?: string;
  value: TValue;
  label: ReactElement | string;
  style?: CSSProperties;
}

export interface U21TabsProps<TValue extends TabValue = TabValue> extends Omit<
  HTMLProps<HTMLDivElement>,
  'action'
> {
  action?: ReactNode;
  defaultValue?: TValue;
  onTabChange?: (value: TValue, e?: SyntheticEvent) => void;
  tabs: (U21TabItem<TValue> | ReactElement)[];
  value: TValue;
}

export const U21Tabs = <TValue extends TabValue>(
  props: U21TabsProps<TValue>,
) => {
  const { action, defaultValue, onTabChange, tabs, value, ...rest } = props;

  // filter out non-tabs (ReactElement) and disabled tabs
  const validTabs = useMemo(
    () =>
      tabs.filter((i): i is U21TabItem<TValue> => {
        if (isValidElement<any>(i)) {
          return false;
        }
        return !i.disabled;
      }),
    [tabs],
  );

  const currentTab = useMemo(
    () => validTabs.find((i) => i.value === value),
    [validTabs, value],
  );

  // if no default route, find first enabled tab
  const defaultTab = defaultValue ?? validTabs[0]?.value;

  const onTabChangeRef = useRef(onTabChange);
  onTabChangeRef.current = onTabChange;

  useEffect(() => {
    if (
      onTabChangeRef.current &&
      currentTab === undefined &&
      defaultTab !== undefined
    ) {
      onTabChangeRef.current(defaultTab);
    }
  }, [currentTab, defaultTab]);

  // ---- overflow measurement ----
  const containerRef = useRef<HTMLDivElement>(null);
  const rightContainerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Record<number, HTMLElement | null>>({});
  // cached widths persist across renders so overflowed entries still count
  const widthsRef = useRef<Record<number, number>>({});
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [visibleCount, setVisibleCount] = useState(tabs.length);

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const available = container.clientWidth;
    // 0 width (e.g. jsdom) — keep everything visible
    if (available === 0) {
      return;
    }
    Object.entries(itemRefs.current).forEach(([index, node]) => {
      if (node) {
        widthsRef.current[Number(index)] = node.offsetWidth;
      }
    });
    const rightWidth = rightContainerRef.current?.offsetWidth ?? 0;
    const baseAvailable = available - rightWidth;
    const triggerWidth = triggerRef.current?.offsetWidth ?? 0;
    let totalWidth = 0;
    for (let i = 0; i < tabs.length; i += 1) {
      totalWidth += widthsRef.current[i] ?? 0;
    }
    // everything fits without a trigger — show all tabs, no overflow menu
    if (totalWidth <= baseAvailable) {
      setVisibleCount(tabs.length);
      return;
    }
    let used = 0;
    let count = 0;
    for (let i = 0; i < tabs.length; i += 1) {
      const width = widthsRef.current[i] ?? 0;
      // reserve the trigger unless this is the last entry
      const budget =
        i < tabs.length - 1 ? baseAvailable - triggerWidth : baseAvailable;
      if (used + width > budget) {
        break;
      }
      used += width;
      count += 1;
    }
    setVisibleCount(count);
  }, [tabs.length]);

  const measureRef = useRef(measure);
  measureRef.current = measure;

  // stable signature — callers pass a fresh tabs array each render, so keying
  // effects on `tabs` identity would thrash
  const tabsKey = tabs
    .map((i) => (isValidElement<any>(i) ? 'el' : i.value))
    .join('|');

  // show all on tabs change so the next pass remeasures (don't clear refs/widths
  // here — ref callbacks fire first, this would wipe what measure() reads)
  useLayoutEffect(() => {
    setVisibleCount(tabs.length);
  }, [tabsKey, tabs.length]);

  useLayoutEffect(() => {
    measureRef.current();
  }, [tabsKey, visibleCount, value]);

  // watch the container plus every width-bearing child and the right-side
  // controls — a label/icon/action can change width while values (tabsKey) and
  // the container size stay fixed, which would otherwise leave widthsRef stale
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }
    const observer = new ResizeObserver(() => measureRef.current());
    observer.observe(container);
    Object.values(itemRefs.current).forEach((node) => {
      if (node) {
        observer.observe(node);
      }
    });
    if (rightContainerRef.current) {
      observer.observe(rightContainerRef.current);
    }
    return () => observer.disconnect();
  }, [tabsKey, visibleCount]);

  const overflowedValues = useMemo(() => {
    const set = new Set<TabValue>();
    tabs.forEach((tab, i) => {
      if (isValidElement<any>(tab)) {
        return;
      }
      if (i >= visibleCount) {
        set.add(tab.value);
      }
    });
    return set;
  }, [tabs, visibleCount]);

  const currentTabOverflowed = Boolean(
    currentTab && overflowedValues.has(currentTab.value),
  );

  const overflowMenuItems = useMemo<U21MenuButtonItems>(
    () =>
      validTabs
        .filter((i) => overflowedValues.has(i.value))
        .map((i) => ({
          key: i.value,
          text: i.label,
          onClick: (e) => onTabChangeRef.current?.(i.value, e),
          to: i.link ? `${i.value}${i.search ?? ''}` : undefined,
          rightIcon:
            currentTab?.value === i.value ? <StyledIconCheck /> : undefined,
        })),
    [currentTab?.value, overflowedValues, validTabs],
  );

  return (
    <TabsContainer ref={containerRef} {...getDOMProps(rest)}>
      <Tabs
        onChange={(e, newValue) => onTabChange?.(newValue, e)}
        value={currentTabOverflowed ? false : (currentTab?.value ?? false)}
        variant="standard"
      >
        {tabs.map((tab, index) => {
          // ref on the Tab/element directly so MUI still sees Tab as a child
          if (index >= visibleCount) {
            return null;
          }
          const setRef = (node: HTMLElement | null) => {
            itemRefs.current[index] = node;
          };
          if (isValidElement<any>(tab)) {
            return cloneElement(tab, {
              key: tab.key ?? `el-${index}`,
              ref: setRef,
            });
          }
          const {
            disabled,
            icon,
            link,
            search,
            style,
            value: tabValue,
            ...restTab
          } = tab;
          return (
            <StyledTab
              ref={setRef}
              $style={style}
              key={tabValue}
              disabled={disabled}
              icon={icon && <IconContainer>{icon}</IconContainer>}
              iconPosition="start"
              value={tabValue}
              {...restTab}
              {...(link && !disabled
                ? { LinkComponent: Link, to: `${tabValue}${search ?? ''}` }
                : {})}
            />
          );
        })}
      </Tabs>
      {Boolean(overflowMenuItems.length) && (
        <StyledU21MenuButton
          ref={triggerRef}
          $tabChosen={currentTabOverflowed}
          alignRight
          buttonProps={{ size: 'small' }}
          items={overflowMenuItems}
          showArrows
        >
          {currentTabOverflowed ? <>{currentTab?.label}</> : 'More'}
        </StyledU21MenuButton>
      )}
      {action && (
        <ActionContainer
          key="actions"
          ref={rightContainerRef}
          horizontal
          useFlexGap
        >
          {action}
        </ActionContainer>
      )}
    </TabsContainer>
  );
};

const TabsContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  min-width: 0;
`;

const StyledIconCheck = styled(IconCheck)`
  ${(props) => css`
    color: ${props.theme.palette.primary.main};
  `}
`;

const StyledTab = styled(Tab)<{ $style?: CSSProperties }>`
  padding: 0 12px;
  border-radius: 8px;

  &&& {
    margin: 0;
    pointer-events: auto;
  }

  // spread to get around a typing issue
  ${(props) => ({ ...props.$style })}

  // override hover styles for anchor tag
    ${(props) =>
    !props.disabled &&
    css`
      &:hover {
        color: ${props.theme.palette.text.secondary};
      }

      &.Mui-selected:hover {
        color: ${props.theme.palette.text.primary};
      }
    `}
`;

export const IconContainer = styled.span`
  display: flex;
  margin-right: 8px;

  &&& {
    margin-bottom: 0;
  }
`;

const StyledU21MenuButton = styled(U21MenuButton)<{ $tabChosen: boolean }>`
  // 12px is consistent the tab button padding
  margin-left: 12px;

  ${(props) =>
    props.$tabChosen
      ? css`
          border: 1px solid ${props.theme.palette.primary.main};
        `
      : css``}}
`;

const ActionContainer = styled(U21Spacer)`
  flex-shrink: 0;
  margin-left: auto;
  padding-left: 8px;
`;
